/**
 * Offer codes: from an announcement's link (?offer=CODE, kept in this browser until used or removed)
 * or typed in Settings → Plan. Choosing a plan sends it; the server checks it.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift, X } from "lucide-react";
import { get } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KEY = "brainlog-offer";

export function rememberOfferFromUrl() {
  try {
    const code = new URLSearchParams(window.location.search).get("offer");
    if (code && /^[A-Za-z0-9_-]{2,40}$/.test(code)) localStorage.setItem(KEY, code.toUpperCase());
  } catch {
    /* storage unavailable */
  }
}
export function currentOffer(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}
function setOffer(code: string | null) {
  try {
    if (code) localStorage.setItem(KEY, code.toUpperCase());
    else localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
}

interface OfferInfo {
  code: string;
  text: string;
  until: string;
  open: boolean;
  usable: boolean | null;
  reason: string | null;
}

export function useOffer() {
  const [code, setCode] = useState(currentOffer);
  const q = useQuery({ queryKey: ["offer", code], queryFn: () => get<OfferInfo>(`/api/offer?code=${encodeURIComponent(code!)}`), enabled: !!code, retry: false });
  return {
    code,
    info: q.data ?? null,
    invalid: q.isError,
    set: (c: string | null) => {
      setOffer(c);
      setCode(c ? c.toUpperCase() : null);
    },
  };
}

const untilText = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

/** The offer being used, above the plans; or, in Settings, a field to type a code. */
export function OfferBanner({ allowEntry = false }: { allowEntry?: boolean }) {
  const offer = useOffer();
  const qc = useQueryClient();
  const [typed, setTyped] = useState("");
  if (!offer.code) {
    if (!allowEntry) return null;
    return (
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (typed.trim()) offer.set(typed.trim());
        }}
      >
        <label htmlFor="offer-code" className="text-xs text-muted-foreground">
          Offer code
        </label>
        <Input id="offer-code" value={typed} onChange={(e) => setTyped(e.target.value)} className="h-8 w-40 font-mono uppercase" maxLength={40} />
        <Button size="xs" variant="outline" type="submit" disabled={!typed.trim()}>
          Apply
        </Button>
      </form>
    );
  }
  const bad = offer.invalid || (offer.info && (!offer.info.open || offer.info.usable === false));
  return (
    <div role="status" className={bad ? "flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm" : "flex flex-wrap items-center gap-2 rounded-md border border-warning/50 bg-warning/10 p-2 text-sm"}>
      <Gift className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        {offer.invalid ? (
          <>The offer code {offer.code} is not known.</>
        ) : !offer.info ? (
          <>Offer {offer.code}…</>
        ) : !offer.info.open ? (
          <>The offer {offer.code} has ended.</>
        ) : offer.info.usable === false ? (
          <>
            Offer {offer.code}: {offer.info.reason}
          </>
        ) : (
          <>
            <strong>Offer {offer.code}:</strong> {offer.info.text}, until {untilText(offer.info.until)}. It is applied when you choose a plan.
          </>
        )}
      </span>
      <Button
        size="xs"
        variant="ghost"
        onClick={() => {
          offer.set(null);
          void qc.invalidateQueries({ queryKey: ["offer"] });
        }}
      >
        <X /> {bad ? "Remove" : "Do not use it"}
      </Button>
    </div>
  );
}
